import { useEffect, useState, useCallback, useRef } from 'react';
import {
  cxGet, cxPost, useCxEntity, Panel, fmtNum, timeAgo, authHeaders,
  SPECTRUM_META, TIER_META, tierClass,
} from './cxShared';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  PaperPlaneTilt, EnvelopeSimple, ChatCircleText, Clock,
  Flask, Warning, CaretDown, CaretRight, ArrowClockwise, CheckCircle, Sparkle, DownloadSimple,
  ShieldCheck, XCircle, Hourglass, Info,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const PERF = ['Excellent', 'Good', 'Normal', 'Dormant'];
const TIERS = ['Gold', 'Silver', 'Bronze'];
const MERGE_TAGS = ['first_name', 'name', 'tier', 'balance', 'last_refill', 'days_since', 'refills', 'bottles', 'entity'];
const FAMILY_LABEL = { winback: 'Win-back & retention', activation: 'Activation', loyalty: 'Loyalty & value', broadcast: 'Broadcast' };

const emptyFilters = () => ({
  spectrum: [], tier: [], performance: [], campus: '', category: '',
  wallet_min: '', wallet_max: '', days_since_min: '', days_since_max: '',
  refills_min: '', refills_max: '', topped_up: 'any', low_balance: false,
  registered_from: '', registered_to: '',
});

// client-side merge preview from a sample recipient row
const renderPreview = (text, s) => {
  if (!text) return '';
  const first = (s?.name || '').trim().split(' ')[0] || 'there';
  const ccy = s?.currency || '';
  const bal = `${ccy ? ccy + ' ' : ''}${Math.round(Number(s?.wallet_balance || 0)).toLocaleString()}`;
  const map = {
    first_name: first, name: s?.name || 'there',
    tier: s?.tier || '', balance: bal, currency: ccy,
    last_refill: (s?.last_refill || '').slice(0, 10) || '-',
    days_since: s?.days_since ?? '', refills: s?.total_refills ?? '', bottles: '—', entity: 'Ecowater',
  };
  return text.replace(/\{(\w+)\}/g, (m, k) => (k in map && map[k] !== '' ? map[k] : m));
};

export default function CxCampaigns() {
  const { entityId } = useCxEntity();
  const [view, setView] = useState('new');
  return (
    <div className="space-y-6" data-testid="cx-campaigns-page">
      <div className="flex items-center gap-2">
        <TabBtn active={view === 'new'} onClick={() => setView('new')} testid="campaigns-tab-new"><Sparkle size={15} className="mr-1.5" />New campaign</TabBtn>
        <TabBtn active={view === 'history'} onClick={() => setView('history')} testid="campaigns-tab-history"><Clock size={15} className="mr-1.5" />History</TabBtn>
      </div>
      {view === 'new' ? <NewCampaign entityId={entityId} onSent={() => setView('history')} /> : <History entityId={entityId} />}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`inline-flex items-center px-4 h-9 rounded-lg text-sm font-semibold transition
      ${active ? 'bg-[#0A2540] text-white' : 'bg-white border border-[#EDE6D8] text-[#44403C] hover:bg-[#FCFAF6]'}`}>
    {children}
  </button>
);

function NewCampaign({ entityId, onSent }) {
  const { user } = useAuth();
  const isAgent = (user?.role || '') === 'cx_agent';
  const [templates, setTemplates] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [approverId, setApproverId] = useState('');
  const [name, setName] = useState('');
  const [family, setFamily] = useState('broadcast');
  const [templateKey, setTemplateKey] = useState('');
  const [channel, setChannel] = useState('sms');
  const [sms, setSms] = useState('');
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [filters, setFilters] = useState(emptyFilters());
  const [showMore, setShowMore] = useState(false);
  const [preview, setPreview] = useState({ total: 0, reachable_sms: 0, reachable_email: 0, sample: [] });
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [testMobile, setTestMobile] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [dlAud, setDlAud] = useState(false);
  const [lowInfo, setLowInfo] = useState({ threshold: null, currency: '', multi: false });
  const debRef = useRef(null);

  const scopeId = entityId && entityId !== 'all' ? entityId : undefined;

  useEffect(() => {
    cxGet('/cx/templates').then((r) => setTemplates(r.templates || [])).catch(() => {});
    const eq = scopeId ? `?entity_id=${scopeId}` : '';
    cxGet(`/campuses${eq}`).then((r) => setCampuses(Array.isArray(r) ? r : [])).catch(() => {});
    cxGet('/subscriber-categories').then((r) => setCategories(Array.isArray(r) ? r : [])).catch(() => {});
    cxGet(`/cx/targets${eq}`).then((r) => {
      const ts = r.targets || [];
      if (ts.length === 1) setLowInfo({ threshold: Number(ts[0].low_balance_threshold || 0), currency: ts[0].currency_code || '', multi: false });
      else setLowInfo({ threshold: null, currency: '', multi: true });
    }).catch(() => {});
    if (isAgent) {
      cxGet('/cx/campaigns/approvers').then((r) => {
        const a = r.approvers || [];
        setApprovers(a);
        if (a.length && !approverId) setApproverId(a[0].id);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, isAgent]);

  const buildAudience = useCallback(() => {
    const a = { entity_id: scopeId };
    ['spectrum', 'tier', 'performance'].forEach((k) => { if (filters[k].length) a[k] = filters[k]; });
    if (filters.campus) a.campus = filters.campus;
    if (filters.category) a.category = filters.category;
    if (filters.topped_up !== 'any') a.topped_up = filters.topped_up;
    if (filters.low_balance) a.low_balance = true;
    ['wallet_min', 'wallet_max', 'days_since_min', 'days_since_max', 'refills_min', 'refills_max'].forEach((k) => {
      if (filters[k] !== '' && filters[k] != null) a[k] = Number(filters[k]);
    });
    if (filters.registered_from) a.registered_from = filters.registered_from;
    if (filters.registered_to) a.registered_to = filters.registered_to;
    return a;
  }, [filters, scopeId]);

  // live preview (debounced)
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setPreviewing(true);
      try { setPreview(await cxPost('/cx/campaigns/preview', buildAudience())); }
      catch { /* noop */ }
      finally { setPreviewing(false); }
    }, 450);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [buildAudience]);

  const applyTemplate = (key) => {
    setTemplateKey(key);
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    setName(t.name);
    setFamily(t.family || 'broadcast');
    setSms(t.sms_body || '');
    setSubject(t.email_subject || '');
    setEmailBody(t.email_body || '');
    if ((t.channels || []).length === 1) setChannel(t.channels[0]);
    else setChannel('sms');
    const a = t.audience || {};
    setFilters({ ...emptyFilters(), ...normPreset(a) });
    setShowMore(Boolean(a.wallet_max != null || a.days_since_min != null || a.refills_min != null || a.refills_max != null || a.topped_up || a.low_balance));
  };

  const toggle = (key, val) => setFilters((f) => ({
    ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));

  const sample = preview.sample?.[0];
  const smsLen = sms.length;
  const smsSegs = Math.max(1, Math.ceil(smsLen / 160));

  const validate = () => {
    if (!name.trim()) { toast.error('Give the campaign a name'); return false; }
    if (channel !== 'email' && !sms.trim()) { toast.error('SMS message is empty'); return false; }
    if (channel !== 'sms' && (!subject.trim() || !emailBody.trim())) { toast.error('Email subject and body are required'); return false; }
    if (filters.low_balance && !lowInfo.multi && !(lowInfo.threshold > 0)) { toast.error('Set a Low-balance threshold in Configurations → CX first'); return false; }
    if (!preview.total) { toast.error('Audience is empty — adjust your filters'); return false; }
    return true;
  };

  const doSend = async () => {
    setSending(true);
    try {
      const r = await cxPost('/cx/campaigns', {
        name, channel, family, template_key: templateKey || null,
        sms_body: sms, email_subject: subject, email_body: emailBody,
        audience: buildAudience(),
        approver_id: isAgent ? (approverId || null) : null,
      });
      if (isAgent) {
        const ap = approvers.find((a) => a.id === approverId);
        toast.success(`Sent to ${ap?.name || 'admin'} for approval`);
      } else {
        toast.success(`Sending to ${fmtNum(r.recipients_total)} customers`);
      }
      setConfirmOpen(false); setAck(false); setApproveOpen(false);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create campaign');
    } finally { setSending(false); }
  };

  const sendTest = async () => {
    try {
      const body = { channel: channel === 'email' ? 'email' : 'sms', sms_body: sms, email_subject: subject, email_body: emailBody, entity_id: scopeId };
      if (body.channel === 'sms') { if (!testMobile) return toast.error('Enter a test mobile'); body.to_mobile = testMobile; }
      else { if (!testEmail) return toast.error('Enter a test email'); body.to_email = testEmail; }
      const r = await cxPost('/cx/campaigns/test', body);
      if (r.status === 'sent') toast.success('Test sent');
      else toast.error(`Test ${r.status}: ${r.error || 'gateway error'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
  };

  const downloadAudience = async () => {
    setDlAud(true);
    try {
      const res = await axios.post(`${API_URL}/api/cx/campaigns/audience/export`, buildAudience(), { headers: authHeaders(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `campaign-audience_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
      toast.success('Audience downloaded');
    } catch (e) { toast.error('Download failed'); }
    finally { setDlAud(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* LEFT: audience + composer */}
      <div className="lg:col-span-3 space-y-6">
        <Panel title="1 · Start by selecting a campaign" testid="campaigns-template-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Template</Label>
              <Select value={templateKey} onValueChange={applyTemplate}>
                <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="template-select"><SelectValue placeholder="Start from scratch or pick a template" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {['winback', 'activation', 'loyalty', 'broadcast'].map((fam) => (
                    <div key={fam}>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#A8A29E]">{FAMILY_LABEL[fam]}</div>
                      {templates.filter((t) => t.family === fam).map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Campaign name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. June win-back" className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="campaign-name" />
            </div>
          </div>
        </Panel>

        <Panel title="2 · Build your audience" testid="campaigns-audience-panel">
          <div className="space-y-4">
            <ChipRow label="Dormancy spectrum" opts={Object.keys(SPECTRUM_META).map((k) => [k, `${SPECTRUM_META[k].label}`])} sel={filters.spectrum} onToggle={(v) => toggle('spectrum', v)} testid="chip-spectrum" />
            <ChipRow label="Tier" opts={TIERS.map((t) => [t, t])} sel={filters.tier} onToggle={(v) => toggle('tier', v)} testid="chip-tier" />
            <ChipRow label="Performance" opts={PERF.map((p) => [p, p])} sel={filters.performance} onToggle={(v) => toggle('performance', v)} testid="chip-performance" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SelWrap label="Campus" value={filters.campus} onChange={(v) => setFilters({ ...filters, campus: v })} placeholder="All campuses" opts={campuses.map((c) => c.campus_name)} testid="filter-campus" />
              <SelWrap label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} placeholder="All categories" opts={categories.map((k) => k.category)} testid="filter-category" />
            </div>
            <button onClick={() => setShowMore(!showMore)} className="inline-flex items-center text-xs font-semibold text-[#0A2540]" data-testid="toggle-more-filters">
              {showMore ? <CaretDown size={14} className="mr-1" /> : <CaretRight size={14} className="mr-1" />} More filters
            </button>
            {showMore && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-1">
                <NumWrap label="Wallet min" value={filters.wallet_min} onChange={(v) => setFilters({ ...filters, wallet_min: v })} testid="wallet-min" />
                <NumWrap label="Wallet max" value={filters.wallet_max} onChange={(v) => setFilters({ ...filters, wallet_max: v })} testid="wallet-max" />
                <NumWrap label="Idle days min" value={filters.days_since_min} onChange={(v) => setFilters({ ...filters, days_since_min: v })} testid="days-min" />
                <NumWrap label="Idle days max" value={filters.days_since_max} onChange={(v) => setFilters({ ...filters, days_since_max: v })} testid="days-max" />
                <NumWrap label="Refills min" value={filters.refills_min} onChange={(v) => setFilters({ ...filters, refills_min: v })} testid="refills-min" />
                <NumWrap label="Refills max" value={filters.refills_max} onChange={(v) => setFilters({ ...filters, refills_max: v })} testid="refills-max" />
                <SelWrap label="Topped up" value={filters.topped_up === 'any' ? '' : filters.topped_up} onChange={(v) => setFilters({ ...filters, topped_up: v || 'any' })} placeholder="Any" opts={[['yes', 'Has topped up'], ['no', 'Never topped up']]} pairs testid="filter-toppedup" />
                <label className="flex items-start gap-2 col-span-2 sm:col-span-3 cursor-pointer bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg p-3" data-testid="filter-low-balance-wrap">
                  <Checkbox checked={filters.low_balance} onCheckedChange={(v) => setFilters({ ...filters, low_balance: Boolean(v) })} data-testid="filter-low-balance" className="mt-0.5" />
                  <span className="text-xs text-[#44403C]">
                    <b>Low balance only</b> — customers at or below the configured threshold.
                    {!lowInfo.multi && (lowInfo.threshold > 0
                      ? <span className="text-[#78716C]"> Currently {lowInfo.currency} {Number(lowInfo.threshold).toLocaleString()}.</span>
                      : <span className="text-[#B4794A]"> Not set — configure it in Configurations → CX.</span>)}
                    {lowInfo.multi && <span className="text-[#78716C]"> Uses each entity's own threshold.</span>}
                  </span>
                </label>
                <div>
                  <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Registered from</Label>
                  <Input type="date" value={filters.registered_from} onChange={(e) => setFilters({ ...filters, registered_from: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="reg-from" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Registered to</Label>
                  <Input type="date" value={filters.registered_to} onChange={(e) => setFilters({ ...filters, registered_to: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="reg-to" />
                </div>
              </div>
            )}
            <button onClick={() => setFilters(emptyFilters())} className="text-xs text-[#78716C] underline" data-testid="clear-filters">Clear all filters</button>
          </div>
        </Panel>

        <Panel title="3 · Compose the message" testid="campaigns-compose-panel">
          <div className="flex items-center gap-2 mb-4">
            {[['sms', 'SMS', ChatCircleText], ['email', 'Email', EnvelopeSimple], ['both', 'SMS + Email', PaperPlaneTilt]].map(([v, l, Ic]) => (
              <button key={v} onClick={() => setChannel(v)} data-testid={`channel-${v}`}
                className={`inline-flex items-center px-3 h-8 rounded-lg text-xs font-semibold transition ${channel === v ? 'bg-[#0A2540] text-white' : 'bg-white border border-[#EDE6D8] text-[#44403C]'}`}>
                <Ic size={14} className="mr-1.5" />{l}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {MERGE_TAGS.map((t) => (
              <button key={t} onClick={() => insertTag(channel === 'email' ? setEmailBody : setSms, t)}
                className="px-2 py-0.5 rounded-md bg-[#FCFAF6] border border-[#EDE6D8] text-[10px] font-mono text-[#0A2540] hover:bg-[#EDE6D8]" data-testid={`tag-${t}`}>{`{${t}}`}</button>
            ))}
          </div>
          {channel !== 'email' && (
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">SMS message</Label>
                <span className="text-[10px] text-[#A8A29E]">{smsLen} chars · {smsSegs} SMS</span>
              </div>
              <Textarea value={sms} onChange={(e) => setSms(e.target.value)} rows={3} className="border-[#EDE6D8] rounded-lg mt-1" data-testid="sms-body" />
              {sample && <p className="text-[11px] text-[#78716C] mt-1.5"><b className="text-[#0A2540]">Preview:</b> {renderPreview(sms, sample)}</p>}
            </div>
          )}
          {channel !== 'sms' && (
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Email subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="email-subject" />
              </div>
              <div>
                <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Email body</Label>
                <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6} className="border-[#EDE6D8] rounded-lg mt-1" data-testid="email-body" />
                {sample && <p className="text-[11px] text-[#78716C] mt-1.5"><b className="text-[#0A2540]">Preview:</b> {renderPreview(subject, sample)} — {renderPreview(emailBody, sample).slice(0, 140)}…</p>}
              </div>
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-[#EDE6D8]">
            <Label className="text-[10px] text-[#78716C] uppercase tracking-wider flex items-center"><Flask size={13} className="mr-1" />Send a test to yourself</Label>
            <div className="flex gap-2 mt-1.5">
              {channel === 'email'
                ? <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="border-[#EDE6D8] rounded-lg h-9" data-testid="test-email" />
                : <Input value={testMobile} onChange={(e) => setTestMobile(e.target.value)} placeholder="2557..." className="border-[#EDE6D8] rounded-lg h-9" data-testid="test-mobile" />}
              <Button variant="outline" onClick={sendTest} className="border-[#EDE6D8] rounded-lg h-9 shrink-0" data-testid="send-test">Send test</Button>
            </div>
          </div>
        </Panel>
      </div>

      {/* RIGHT: audience summary + send */}
      <div className="lg:col-span-2">
        <div className="sticky top-4 space-y-4">
          <Panel title="Audience" testid="campaigns-summary-panel">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-[#0A2540]" data-testid="audience-total">{previewing ? '…' : fmtNum(preview.total)}</span>
              <span className="text-sm text-[#78716C]">customers</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl bg-[#FCFAF6] border border-[#EDE6D8] p-3">
                <div className="flex items-center text-[11px] text-[#78716C]"><ChatCircleText size={13} className="mr-1" />SMS reachable</div>
                <div className="text-xl font-bold text-[#0A2540]" data-testid="reachable-sms">{fmtNum(preview.reachable_sms)}</div>
              </div>
              <div className="rounded-xl bg-[#FCFAF6] border border-[#EDE6D8] p-3">
                <div className="flex items-center text-[11px] text-[#78716C]"><EnvelopeSimple size={13} className="mr-1" />Email reachable</div>
                <div className="text-xl font-bold text-[#0A2540]" data-testid="reachable-email">{fmtNum(preview.reachable_email)}</div>
              </div>
            </div>
            <Button variant="outline" onClick={downloadAudience} disabled={dlAud || previewing || !preview.total} className="w-full mt-4 border-[#EDE6D8] rounded-lg h-9 text-[#0A2540]" data-testid="download-audience">
              <DownloadSimple size={15} className="mr-1.5" />{dlAud ? 'Preparing…' : 'Download audience (Excel)'}
            </Button>
            {preview.sample?.length > 0 && (
              <div className="mt-4 max-h-52 overflow-y-auto rounded-lg border border-[#EDE6D8]">
                <table className="w-full text-[11px]">
                  <tbody>
                    {preview.sample.map((s) => (
                      <tr key={s.sub_id} className="border-b border-[#F1EBE0] last:border-0">
                        <td className="px-2 py-1.5 font-medium text-[#1C1917]">{s.name || '—'}</td>
                        <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${tierClass(s.tier)}`}>{s.tier}</span></td>
                        <td className="px-2 py-1.5 text-[#78716C] text-right">{s.mobile || s.email || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Send" testid="campaigns-send-panel">
            <div className="space-y-3">
              {isAgent ? (
                <>
                  <div className="rounded-xl bg-[#FCFAF6] border border-[#EDE6D8] p-3 flex items-start gap-2">
                    <ShieldCheck size={16} className="text-[#0A2540] mt-0.5 shrink-0" />
                    <p className="text-[11px] text-[#57534E] leading-snug">Campaigns need an admin's approval before any message is sent. Pick who should review it.</p>
                  </div>
                  <div>
                    <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Send to for approval</Label>
                    <Select value={approverId} onValueChange={setApproverId}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="approver-select">
                        <SelectValue placeholder={approvers.length ? 'Choose an approver' : 'No approvers available'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {approvers.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.name}{a.role === 'super_admin' ? ' · Super admin' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => { if (!approverId) return toast.error('Choose an approver'); if (validate()) setApproveOpen(true); }}
                    disabled={sending || !approvers.length}
                    className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-10" data-testid="btn-send-approval">
                    <PaperPlaneTilt size={16} className="mr-1.5" />Send for approval
                  </Button>
                  <p className="text-[10px] text-[#A8A29E] leading-snug">The approver gets an email to review the full audience and message, then approves or rejects it.</p>
                </>
              ) : (
                <>
                  <Button onClick={() => validate() && (setAck(false), setConfirmOpen(true))} disabled={sending} className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-10" data-testid="btn-send-now">
                    <PaperPlaneTilt size={16} className="mr-1.5" />Send now
                  </Button>
                  <p className="text-[10px] text-[#A8A29E] leading-snug">Messages are sent through your live SMS gateway and email. Customers without the chosen contact are skipped.</p>
                </>
              )}
            </div>
          </Panel>
        </div>
      </div>

      {/* Confirm modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center text-[#0A2540]"><Warning size={20} weight="fill" className="mr-2 text-[#B4794A]" />Send real messages?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#44403C]">
            <p>This will send <b>"{name}"</b> to:</p>
            <div className="rounded-xl bg-[#FCFAF6] border border-[#EDE6D8] p-3 space-y-1">
              <div className="flex justify-between"><span className="text-[#78716C]">Total in audience</span><b>{fmtNum(preview.total)}</b></div>
              {channel !== 'email' && <div className="flex justify-between"><span className="text-[#78716C]">via SMS</span><b>{fmtNum(preview.reachable_sms)}</b></div>}
              {channel !== 'sms' && <div className="flex justify-between"><span className="text-[#78716C]">via Email</span><b>{fmtNum(preview.reachable_email)}</b></div>}
            </div>
            <p className="text-[12px] text-[#B4794A]">These are real customers. This action cannot be undone.</p>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={ack} onCheckedChange={setAck} data-testid="confirm-ack" className="mt-0.5" />
              <span className="text-[12px]">I understand real SMS/email will be sent to these customers.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-lg border-[#EDE6D8]">Cancel</Button>
            <Button onClick={() => doSend()} disabled={!ack || sending} className="rounded-lg bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="confirm-send-btn">
              {sending ? 'Sending…' : `Send to ${fmtNum(preview.total)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send-for-approval modal (CX agents) */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="sm:max-w-md" data-testid="send-approval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center text-[#0A2540]"><ShieldCheck size={20} weight="fill" className="mr-2 text-[#0A2540]" />Send for approval</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#44403C]">
            <p>Submit <b>"{name}"</b> to <b>{approvers.find((a) => a.id === approverId)?.name || 'the selected admin'}</b> for approval.</p>
            <div className="rounded-xl bg-[#FCFAF6] border border-[#EDE6D8] p-3 space-y-1">
              <div className="flex justify-between"><span className="text-[#78716C]">Total in audience</span><b>{fmtNum(preview.total)}</b></div>
              {channel !== 'email' && <div className="flex justify-between"><span className="text-[#78716C]">via SMS</span><b>{fmtNum(preview.reachable_sms)}</b></div>}
              {channel !== 'sms' && <div className="flex justify-between"><span className="text-[#78716C]">via Email</span><b>{fmtNum(preview.reachable_email)}</b></div>}
            </div>
            <p className="text-[12px] text-[#78716C]">No messages are sent yet. The admin will get an email and can review, then approve or reject.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} className="rounded-lg border-[#EDE6D8]">Cancel</Button>
            <Button onClick={() => doSend()} disabled={sending} className="rounded-lg bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="confirm-approval-btn">
              {sending ? 'Submitting…' : 'Send for approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function History({ entityId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const scopeId = entityId && entityId !== 'all' ? entityId : undefined;
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await cxGet(`/cx/campaigns${scopeId ? `?entity_id=${scopeId}` : ''}`); setRows(r.campaigns || []); }
    catch { /* noop */ } finally { setLoading(false); }
  }, [scopeId]);
  useEffect(() => { load(); }, [load]);

  return (
    <Panel title="Campaign history" testid="campaigns-history-panel"
      actions={<Button variant="outline" size="sm" onClick={load} className="border-[#EDE6D8] rounded-lg h-8"><ArrowClockwise size={14} className="mr-1" />Refresh</Button>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#A8A29E] border-b border-[#EDE6D8]">
              {['Campaign', 'Channel', 'Audience', 'Sent', 'Failed', 'Converted', 'Status', 'When'].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#78716C]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#78716C]" data-testid="campaigns-empty">No campaigns yet.</td></tr>
            ) : rows.map((c) => (
              <tr key={c.id} className="border-b border-[#F1EBE0] hover:bg-[#FCFAF6]/60 cursor-pointer" onClick={() => setDetail(c.id)} data-testid={`campaign-row-${c.id}`}>
                <td className="px-3 py-2.5 font-medium text-[#1C1917]">{c.name}</td>
                <td className="px-3 py-2.5"><ChannelBadge ch={c.channel} /></td>
                <td className="px-3 py-2.5 text-[#44403C]">{fmtNum(c.recipients_total)}</td>
                <td className="px-3 py-2.5 text-[#3F7D5B] font-semibold">{fmtNum(c.sent_count)}</td>
                <td className="px-3 py-2.5 text-[#B4453A]">{fmtNum((c.failed_count || 0) + (c.blocked_count || 0))}</td>
                <td className="px-3 py-2.5 text-[#0A2540] font-semibold">{fmtNum(c.converted_count ?? c.reactivated_count)}</td>
                <td className="px-3 py-2.5"><StatusBadge s={c.status} /></td>
                <td className="px-3 py-2.5 text-xs text-[#78716C]">{timeAgo(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && <CampaignDetail cid={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </Panel>
  );
}

function CampaignDetail({ cid, onClose, onChanged }) {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'entity_admin'].includes(user?.role || '');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [dl, setDl] = useState(false);
  const load = useCallback(() => { cxGet(`/cx/campaigns/${cid}`).then(setData).catch(() => {}); }, [cid]);
  useEffect(() => { load(); }, [load]);
  const c = data?.campaign;
  const recs = data?.recipients || [];
  const pending = c?.status === 'pending_approval';
  const canAct = pending && isAdmin;

  const approve = async () => {
    setBusy(true);
    try {
      await cxPost(`/cx/campaigns/${cid}/approve`);
      toast.success('Campaign approved — sending now');
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!reason.trim()) return toast.error('Add a reason');
    setBusy(true);
    try {
      await cxPost(`/cx/campaigns/${cid}/reject`, { reason });
      toast.success('Campaign rejected');
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
    finally { setBusy(false); }
  };
  const downloadAudience = async () => {
    setDl(true);
    try {
      const res = await axios.get(`${API_URL}/api/cx/campaigns/${cid}/audience.xlsx`, { headers: authHeaders(), responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([res.data]));
      link.download = `campaign-${cid.slice(0, 8)}-audience.xlsx`;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
    } catch { toast.error('Download failed'); }
    finally { setDl(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="campaign-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2540]">
            {c?.name || 'Campaign'} {c && <StatusBadge s={c.status} />}
          </DialogTitle>
        </DialogHeader>
        {!c ? <p className="text-sm text-[#78716C]">Loading…</p> : (
          <div className="space-y-4">
            <div className="text-[11px] text-[#78716C]">
              By {c.created_by_name || '—'}{c.created_by_role === 'cx_agent' ? ' (CX agent)' : ''} · {timeAgo(c.created_at)}
              {c.approver_name && pending && <> · awaiting <b className="text-[#0A2540]">{c.approver_name}</b></>}
            </div>

            {pending && (
              <div className="rounded-xl border border-[#E4ECF5] bg-[#F4F8FC] p-4 space-y-3" data-testid="review-panel">
                <div className="flex items-center text-[13px] font-semibold text-[#0A2540]"><Hourglass size={15} className="mr-1.5" />Review before sending</div>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Audience" v={fmtNum(c.recipients_total)} />
                  <Stat label="SMS reach" v={fmtNum(c.reachable_sms)} />
                  <Stat label="Email reach" v={fmtNum(c.reachable_email)} />
                </div>
                {c.channel !== 'email' && c.sms_body && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mb-1">SMS message</div>
                    <p className="text-[13px] text-[#1C1917] bg-white border border-[#EDE6D8] rounded-lg p-2.5 whitespace-pre-wrap" data-testid="review-sms">{c.sms_body}</p>
                  </div>
                )}
                {c.channel !== 'sms' && c.email_subject && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mb-1">Email</div>
                    <div className="text-[13px] text-[#1C1917] bg-white border border-[#EDE6D8] rounded-lg p-2.5" data-testid="review-email">
                      <b>{c.email_subject}</b>
                      <p className="mt-1 whitespace-pre-wrap text-[#44403C]">{c.email_body}</p>
                    </div>
                  </div>
                )}
                <Button variant="outline" onClick={downloadAudience} disabled={dl} className="border-[#EDE6D8] rounded-lg h-9 text-[#0A2540]" data-testid="review-download-audience">
                  <DownloadSimple size={15} className="mr-1.5" />{dl ? 'Preparing…' : 'Download audience (Excel)'}
                </Button>
                {canAct ? (
                  rejecting ? (
                    <div className="space-y-2 pt-1">
                      <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Reason for rejection</Label>
                      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="border-[#EDE6D8] rounded-lg" placeholder="Let the agent know what to change" data-testid="reject-reason" />
                      <div className="flex gap-2">
                        <Button onClick={reject} disabled={busy} className="rounded-lg bg-[#B4453A] hover:bg-[#B4453A]/90 text-white h-9" data-testid="confirm-reject-btn"><XCircle size={15} className="mr-1.5" />Confirm rejection</Button>
                        <Button variant="outline" onClick={() => setRejecting(false)} className="rounded-lg border-[#EDE6D8] h-9">Back</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 pt-1">
                      <Button onClick={approve} disabled={busy} className="rounded-lg bg-[#2F6D4B] hover:bg-[#2F6D4B]/90 text-white h-9" data-testid="approve-btn"><CheckCircle size={15} weight="fill" className="mr-1.5" />Approve &amp; send</Button>
                      <Button variant="outline" onClick={() => setRejecting(true)} className="rounded-lg border-[#EDCFCB] text-[#B4453A] h-9" data-testid="reject-btn"><XCircle size={15} className="mr-1.5" />Reject</Button>
                    </div>
                  )
                ) : (
                  <p className="text-[12px] text-[#78716C]">Awaiting approval from {c.approver_name || 'an administrator'}.</p>
                )}
              </div>
            )}

            {c.status === 'rejected' && (
              <div className="rounded-xl border border-[#F6DDD9] bg-[#FCF1EF] p-3 text-[13px] text-[#8A3A30]" data-testid="rejected-panel">
                <b>Rejected{c.rejected_by_name ? ` by ${c.rejected_by_name}` : ''}.</b> {c.rejection_reason || ''}
              </div>
            )}

            {!pending && (
              <>
                {(() => {
                  const goal = c.conversion_goal || 'refill';
                  const what = goal === 'topup'
                    ? 'Customers who topped up their wallet after the send'
                    : 'Customers who refilled after the send';
                  const asOf = c.conversion_as_of ? new Date(c.conversion_as_of).toLocaleDateString() : null;
                  const hint = `${what}${asOf ? ` · metrics as of ${asOf}` : ''}`;
                  return (
                <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat label="Audience" v={fmtNum(c.recipients_total)} />
                  <Stat label="Sent" v={fmtNum(c.sent_count)} tone="green" />
                  <Stat label="Failed" v={fmtNum((c.failed_count || 0) + (c.blocked_count || 0))} tone="red" />
                  <Stat label="Converted" v={fmtNum(c.converted_count ?? c.reactivated_count)} tone="navy" hint={hint} />
                </div>
                <div className="text-[11px] text-[#78716C]">
                  Channel <ChannelBadge ch={c.channel} /> · SMS sent {fmtNum(c.channel_counts?.sms_sent)} · Email sent {fmtNum(c.channel_counts?.email_sent)} · Skipped {fmtNum(c.skipped_count)}
                </div>
                <div className="text-[11px] text-[#A8A29E]">
                  Converted = {goal === 'topup' ? 'topped up after the reminder' : 'refilled after the send'}{asOf ? ` · metrics as of ${asOf}` : ''}. Same-day top-ups may take until the next daily refresh to appear for older campaigns.
                </div>
                <div className="rounded-lg border border-[#EDE6D8] overflow-hidden">
                  <table className="w-full text-[12px]">
                    <thead><tr className="bg-[#FCFAF6] text-left text-[10px] uppercase tracking-wider text-[#A8A29E]">
                      {['Customer', 'Contact', 'Result', 'Converted'].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {recs.slice(0, 200).map((r) => (
                        <tr key={r.sub_id} className="border-t border-[#F1EBE0]">
                          <td className="px-3 py-1.5 font-medium text-[#1C1917]">{r.name || '—'}</td>
                          <td className="px-3 py-1.5 text-[#78716C]">{r.mobile || r.email || '—'}</td>
                          <td className="px-3 py-1.5"><ResultBadge r={r} /></td>
                          <td className="px-3 py-1.5">{(r.converted ?? r.reactivated) ? <span className="inline-flex items-center text-[#3F7D5B] font-semibold"><CheckCircle size={13} weight="fill" className="mr-1" />Yes</span> : <span className="text-[#A8A29E]">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── small pieces ──────────────────────────────────────────────────────────────
const insertTag = (setter, tag) => setter((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}{${tag}}`);
const normPreset = (a) => {
  const out = {};
  ['spectrum', 'tier', 'performance'].forEach((k) => { if (Array.isArray(a[k])) out[k] = a[k]; });
  ['campus', 'category', 'registered_from', 'registered_to'].forEach((k) => { if (a[k]) out[k] = a[k]; });
  ['wallet_min', 'wallet_max', 'days_since_min', 'days_since_max', 'refills_min', 'refills_max'].forEach((k) => { if (a[k] != null) out[k] = a[k]; });
  if (a.topped_up) out.topped_up = a.topped_up;
  if (a.low_balance) out.low_balance = true;
  return out;
};

const ChipRow = ({ label, opts, sel, onToggle, testid }) => (
  <div>
    <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">{label}</Label>
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={testid}>
      {opts.map(([v, l]) => (
        <button key={v} onClick={() => onToggle(v)} data-testid={`${testid}-${v}`}
          className={`px-2.5 h-7 rounded-full text-[11px] font-semibold border transition ${sel.includes(v) ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-[#57534E] border-[#EDE6D8] hover:border-[#0A2540]/40'}`}>
          {l}
        </button>
      ))}
    </div>
  </div>
);

const SelWrap = ({ label, value, onChange, placeholder, opts, pairs, testid }) => (
  <div>
    <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">{label}</Label>
    <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
      <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value="__all__">{placeholder}</SelectItem>
        {opts.map((o) => pairs ? <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem> : <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const NumWrap = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">{label}</Label>
    <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid={testid} />
  </div>
);

const Stat = ({ label, v, tone, hint }) => {
  const c = { green: 'text-[#3F7D5B]', red: 'text-[#B4453A]', navy: 'text-[#0A2540]' }[tone] || 'text-[#1C1917]';
  return (
    <div className="rounded-xl bg-[#FCFAF6] border border-[#EDE6D8] p-3" title={hint || undefined}>
      <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] flex items-center gap-1">
        {label}{hint ? <Info size={11} className="text-[#C4BDB1]" /> : null}
      </div>
      <div className={`text-2xl font-bold ${c}`}>{v}</div>
    </div>
  );
};

const ChannelBadge = ({ ch }) => {
  const map = { sms: 'SMS', email: 'Email', both: 'SMS + Email' };
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#EDE9E1] text-[#44403C]">{map[ch] || ch}</span>;
};
const StatusBadge = ({ s }) => {
  const map = {
    sent: 'bg-[#DCEBE2] text-[#2F6D4B]', sending: 'bg-[#FBF0DC] text-[#8A6D2B]',
    queued: 'bg-[#FBF0DC] text-[#8A6D2B]', scheduled: 'bg-[#E4ECF5] text-[#2C4A6B]',
    pending_approval: 'bg-[#E4ECF5] text-[#2C4A6B]', rejected: 'bg-[#F6DDD9] text-[#8A3A30]',
    cancelled: 'bg-stone-200 text-stone-600', failed: 'bg-[#F6DDD9] text-[#8A3A30]',
  };
  const label = { pending_approval: 'pending approval' }[s] || s;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${map[s] || 'bg-stone-100 text-stone-600'}`}>{label}</span>;
};
const ResultBadge = ({ r }) => {
  const s = r.overall;
  const map = { sent: 'bg-[#DCEBE2] text-[#2F6D4B]', failed: 'bg-[#F6DDD9] text-[#8A3A30]', blocked: 'bg-[#FBF0DC] text-[#8A6D2B]', skipped: 'bg-stone-200 text-stone-500', pending: 'bg-stone-100 text-stone-500' };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${map[s] || 'bg-stone-100 text-stone-600'}`}>{s}</span>;
};
